import { useEffect, useState } from "react";
import { AppIcon } from "@/components/Brand";
import {
  Lock, LogOut, Loader2, ShieldCheck, TrendingUp, Clock, AlertTriangle, MapPin, Users, CloudSun,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL;

const TEAL = "#2BBFB3";
const GOLD = "#F5A623";
const PIE_COLORS = ["#2BBFB3", "#2563EB", "#F5A623", "#E8368F", "#7C3AED"];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const Card = ({ title, icon: Icon, children, testid, className = "" }) => (
  <div data-testid={testid} className={`rounded-2xl border border-white/10 bg-white/[0.03] p-6 ${className}`}>
    <p className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/40 font-bold">
      <Icon size={13} className="text-[#2BBFB3]" /> {title}
    </p>
    <div className="mt-4">{children}</div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/15 bg-[#222] px-3 py-2 text-xs shadow-xl">
      <p className="text-white/50">{label || payload[0].name}</p>
      <p className="font-bold text-white">{payload[0].value.toLocaleString()} lessons</p>
    </div>
  );
};

function RankedBars({ rows, color = TEAL, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-2.5" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={r.label} className="group">
          <div className="flex items-baseline justify-between text-xs mb-1">
            <span className="text-white/75 font-medium">
              <span className="text-white/30 font-mono mr-2">{String(i + 1).padStart(2, "0")}</span>
              {r.label}
            </span>
            <span className="text-white/45 font-mono">{r.count.toLocaleString()}</span>
          </div>
          <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{ width: `${(r.count / max) * 100}%`, background: i === 0 ? GOLD : color, opacity: 1 - i * 0.05 }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FounderInsights() {
  const [auth, setAuth] = useState(null); // null=checking, false=logged out, object=authed
  const [mounted, setMounted] = useState(false); // defers chart mount so ResponsiveContainer measures a laid-out parent
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [data, setData] = useState(null);
  const [dataError, setDataError] = useState("");

  // never indexable
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "DriveAssure — Founder Analytics";
    return () => {
      document.head.removeChild(meta);
      document.title = "DriveAssure — Run your driving school smarter. Not harder.";
    };
  }, []);

  useEffect(() => {
    fetch(`${API}/api/admin/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setAuth)
      .catch(() => setAuth(false));
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!auth) return;
    fetch(`${API}/api/admin/analytics/summary`, { credentials: "include" })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(formatApiErrorDetail(body.detail));
        setData(body);
      })
      .catch((e) => setDataError(e.message));
  }, [auth]);

  const login = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
      setAuth(body);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/api/admin/logout`, { method: "POST", credentials: "include" });
    setAuth(false);
    setData(null);
    setPassword("");
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-[#161616] grid place-items-center">
        <Loader2 size={28} className="animate-spin text-[#2BBFB3]" data-testid="admin-checking" />
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="min-h-screen bg-[#161616] text-white flex items-center justify-center px-5">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.03] p-8"
        >
          <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-[#2BBFB3]/15 border border-[#2BBFB3]/30">
            <AppIcon size={34} />
          </span>
          <h1 className="mt-6 font-display font-extrabold tracking-tight text-2xl">Founder Analytics</h1>
          <p className="mt-2 text-sm text-white/50">Private dashboard. Authorised access only.</p>
          <div className="mt-6 relative">
            <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/35" />
            <input
              type="password"
              required
              autoComplete="current-password"
              data-testid="admin-password-input"
              placeholder="Admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-white/15 bg-white/[0.04] pl-11 pr-4 py-3 text-sm outline-none focus:border-[#2BBFB3] focus:ring-2 focus:ring-[#2BBFB3]/20 placeholder:text-white/30"
            />
          </div>
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-300">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loginLoading || !password}
            data-testid="admin-login-submit"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2BBFB3] px-6 py-3 text-sm font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors disabled:opacity-50"
          >
            {loginLoading && <Loader2 size={15} className="animate-spin" />}
            Sign in
          </button>
          <p className="mt-4 text-center text-[11px] text-white/30 leading-relaxed">
            All data shown is aggregate and anonymised. No individual instructor or learner is ever identifiable.
          </p>
        </form>
      </div>
    );
  }

  const busiest = data?.time_of_day?.reduce((a, b) => (b.count > (a?.count || 0) ? b : a), null);
  const topError = data?.top_errors?.[0];

  return (
    <div className="min-h-screen bg-[#161616] text-white" data-testid="analytics-dashboard">
      {/* header */}
      <header className="border-b border-white/10 bg-[#1B1B1B]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AppIcon size={36} />
            <div>
              <p className="font-display font-extrabold text-lg leading-none">Founder Analytics</p>
              <p className="mt-1 text-xs text-white/40">Aggregate, anonymised lesson insights across all DriveAssure accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {data?.data_source === "sample" && (
              <>
                <span data-testid="sample-data-badge" className="rounded-full bg-[#F5A623]/15 border border-[#F5A623]/40 px-3.5 py-1.5 text-xs font-semibold text-[#F5A623]">
                  Sample data — live pipeline pending
                </span>
                <button
                  data-testid="purge-sample-btn"
                  onClick={async () => {
                    if (!window.confirm("Delete all sample analytics data? Do this once, when the live app pipeline is connected. This cannot be undone.")) return;
                    await fetch(`${API}/api/admin/analytics/buckets`, { method: "DELETE", credentials: "include" });
                    setData(null);
                    window.location.reload();
                  }}
                  className="hidden sm:inline-flex items-center rounded-full border border-[#F5A623]/40 px-3.5 py-1.5 text-xs font-semibold text-[#F5A623]/80 hover:bg-[#F5A623]/10 transition-colors"
                >
                  Clear sample data
                </button>
              </>
            )}
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[#2BBFB3]/10 border border-[#2BBFB3]/30 px-3.5 py-1.5 text-xs font-semibold text-[#2BBFB3]">
              <ShieldCheck size={13} /> Anonymised only
            </span>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-white/70 hover:bg-white/10 transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 sm:px-8 py-8 space-y-5">
        {dataError && (
          <p data-testid="analytics-error" className="rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
            {dataError}
          </p>
        )}
        {!data && !dataError && (
          <div className="py-24 grid place-items-center">
            <Loader2 size={28} className="animate-spin text-[#2BBFB3]" data-testid="analytics-loading" />
          </div>
        )}

        {data && mounted && (
          <>
            {/* summary cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Total lessons logged", value: data.total_lessons.toLocaleString(), icon: TrendingUp, testid: "card-total-lessons" },
                { label: "Months of data", value: data.active_months, icon: Clock, testid: "card-months" },
                { label: "Most common error", value: topError?.label || "—", icon: AlertTriangle, testid: "card-top-error", small: true },
                { label: "Busiest time of day", value: busiest?.label || "—", icon: CloudSun, testid: "card-busiest-time", small: true },
              ].map((c) => (
                <div key={c.testid} data-testid={c.testid} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                  <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-white/40 font-bold">
                    <c.icon size={12} className="text-[#2BBFB3]" /> {c.label}
                  </p>
                  <p className={`mt-3 font-display font-extrabold text-white ${c.small ? "text-xl leading-tight" : "text-3xl"}`}>
                    {c.value}
                  </p>
                </div>
              ))}
            </div>

            {/* trend */}
            <Card title="Lessons logged over time" icon={TrendingUp} testid="chart-trend">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.lessons_by_month} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={TEAL} stopOpacity={0.45} />
                        <stop offset="100%" stopColor={TEAL} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(255,255,255,0.2)" }} />
                    <Area type="monotone" dataKey="count" stroke={TEAL} strokeWidth={2.5} fill="url(#trendFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* demographics + conditions grid */}
            <div className="grid lg:grid-cols-2 gap-5">
              <Card title="Learner age brackets" icon={Users} testid="chart-age">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.age_brackets} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                        {data.age_brackets.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card title="Learner gender" icon={Users} testid="chart-gender">
                <div className="h-56 flex items-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.gender.filter((g) => g.count > 0)}
                        dataKey="count"
                        nameKey="label"
                        innerRadius="55%"
                        outerRadius="85%"
                        paddingAngle={3}
                        strokeWidth={0}
                      >
                        {data.gender.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 pr-4 shrink-0">
                    {data.gender.filter((g) => g.count > 0).map((g, i) => (
                      <div key={g.label} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-white/70">{g.label}</span>
                        <span className="text-white/40 font-mono ml-auto pl-3">{g.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <Card title="Lessons by time of day" icon={Clock} testid="chart-time">
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.time_of_day} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                      <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 11 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                      <Bar dataKey="count" radius={[6, 6, 0, 0]} fill={TEAL} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card title="Road conditions" icon={CloudSun} testid="chart-road">
                <div className="h-56 flex items-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.road_conditions.filter((r) => r.count > 0)}
                        dataKey="count"
                        nameKey="label"
                        innerRadius="55%"
                        outerRadius="85%"
                        paddingAngle={3}
                        strokeWidth={0}
                      >
                        {data.road_conditions.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<ChartTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 pr-4 shrink-0">
                    {data.road_conditions.map((r, i) => (
                      <div key={r.label} className="flex items-center gap-2 text-xs">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                        <span className="text-white/70">{r.label}</span>
                        <span className="text-white/40 font-mono ml-auto pl-3">{r.count.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <p className="sr-only">A lesson can record more than one condition.</p>
                </div>
              </Card>
            </div>

            {/* errors + postcodes */}
            <div className="grid lg:grid-cols-2 gap-5">
              <Card title="Most frequent Identified Errors" icon={AlertTriangle} testid="chart-errors">
                <RankedBars rows={data.top_errors} testid="errors-list" />
              </Card>
              <Card title="Lessons by postcode" icon={MapPin} testid="chart-postcodes">
                <RankedBars rows={data.postcodes} color="#2563EB" testid="postcodes-list" />
                <p className="mt-4 text-[11px] text-white/35 leading-relaxed" data-testid="postcode-privacy-note">
                  Postcodes with fewer than {data.postcode_min_count} lessons are suppressed so no area can be
                  traced to an individual. Location is shown at postcode level only — never addresses.
                </p>
              </Card>
            </div>

            <p className="pt-2 pb-6 text-center text-xs text-white/30" data-testid="analytics-privacy-footer">
              Privacy by design: only aggregate counters are stored — raw lesson records never leave the app
              pipeline and no name, address, age, or licence detail is ever collected here. Data may be used for
              academic research and product development in anonymised form only.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
